import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Leaf, ShoppingBag } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

function Money({ price, mrp }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xl font-heading text-forest">₹{price}</span>
      {mrp && mrp > price && <span className="text-sm text-brown/50 line-through">₹{mrp}</span>}
    </div>
  );
}

function ProductCard({ p }) {
  const feat = (p.wellness_needs && p.wellness_needs[0]) || (p.consumer_intents && p.consumer_intents[0]);
  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid={`share-card-${p.id}`}>
      <div className="h-52 bg-sand">{p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-8 h-8 text-sage" /></div>}</div>
      <div className="p-4">
        <p className="text-[11px] uppercase tracking-wide text-olive">{p.brand}</p>
        <h3 className="font-heading text-lg text-forest leading-tight">{p.name}</h3>
        {p.short_description && <p className="text-sm text-brown/70 mt-1 line-clamp-2">{p.short_description}</p>}
        {feat && <span className="inline-flex items-center gap-1 text-xs bg-sage/20 text-forest px-2 py-0.5 rounded-full mt-2"><Leaf className="w-3 h-3" /> {feat}</span>}
        <div className="mt-3"><Money price={p.price} mrp={p.mrp} /></div>
      </div>
    </div>
  );
}

function ShareShell({ children }) {
  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest"><div className="max-w-5xl mx-auto px-4 h-20 flex items-center"><img src={LOGO_URL} alt="LIVENEAT" className="h-14 w-auto object-contain bg-cream rounded-xl p-1" /></div></header>
      <main className="max-w-5xl mx-auto px-4 py-8">{children}</main>
      <footer className="text-center text-xs text-brown/50 py-8">Curated by LIVENEAT • Nourishing People, Enriching Lives</footer>
    </div>
  );
}

export function ShareProduct() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/public/share/product/${pid}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [pid]);
  if (err) return <ShareShell><p className="text-center text-brown/60 py-16">This product is not available.</p></ShareShell>;
  if (!p) return <ShareShell><div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-forest" /></div></ShareShell>;
  return <ShareShell><div className="max-w-md mx-auto" data-testid="share-product"><ProductCard p={p} /></div></ShareShell>;
}

export function ShareCollection() {
  const { cid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/public/share/collection/${cid}`).then((r) => setData(r.data)).catch(() => setErr(true)); }, [cid]);
  if (err) return <ShareShell><p className="text-center text-brown/60 py-16">This collection is not available.</p></ShareShell>;
  if (!data) return <ShareShell><div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-forest" /></div></ShareShell>;
  return (
    <ShareShell>
      <div data-testid="share-collection">
        <p className="text-[11px] uppercase tracking-widest text-olive">{data.brand_name}</p>
        <h1 className="font-heading text-3xl text-forest mb-6">{data.title}</h1>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{data.items.map((p) => <ProductCard key={p.id} p={p} />)}</div>
      </div>
    </ShareShell>
  );
}
